import { useEffect, useState } from 'react';
import type { Message } from '@/types/conversation';

/**
 * How long an outbound message must exist before a failed delivery status is
 * shown as "Not delivered". The backend retries a provider error for about an
 * hour before failing a message, so a failure this soon is rare (a permanent
 * provider rejection or a carrier status); the grace keeps a status that
 * flickers in the first moments after sending (out-of-order carrier updates)
 * from flashing an alarming badge that then disappears.
 */
export const NOT_DELIVERED_GRACE_MS = 2 * 60_000;

/** When a failed outbound message may be shown as not delivered, or null. */
export function notDeliveredRevealAt(msg: Message): number | null {
  if (msg.direction !== 'outbound' || msg.status !== 'failed') return null;
  const created = new Date(msg.createdAt).getTime();
  if (Number.isNaN(created)) return null;
  return created + NOT_DELIVERED_GRACE_MS;
}

/**
 * True once `msg` is a failed outbound message past the grace period.
 * Re-renders at the reveal moment, so a message that fails within the grace
 * flips to "Not delivered" without waiting for another refetch.
 */
export function useNotDelivered(msg: Message): boolean {
  const revealAt = notDeliveredRevealAt(msg);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (revealAt == null) return;
    const wait = revealAt - Date.now();
    if (wait <= 0) {
      setNow(Date.now());
      return;
    }
    const timer = setTimeout(() => setNow(Date.now()), wait);
    return () => clearTimeout(timer);
  }, [revealAt]);

  return revealAt != null && now >= revealAt;
}
